import { TENANT_IDS } from "../constants/tenantIds";
import { normalizeVendorKey } from "../waste/vendorDataCompleteness";

type FixedTenant = { id: string; name: string };

export type WastePipelineFileSlot = {
  /** Unique key used to map files end-to-end (registry → S3 → local path → pipeline run) */
  key: string;
  /** Display label shown on the UI upload button */
  label: string;
  /** HTML accept attribute for the file input (e.g. ".xlsx,.xls,.csv") */
  accept: string;
  /** Whether this file must be provided to dispatch the pipeline */
  required: boolean;
};

export type WastePipelineConfig = {
  /** Display label, e.g. on the runs grid and in dispatch error messages */
  label: string;
  /**
   * Canonical waste vendor this pipeline parses files from. Matched against
   * `WasteManifestVendor.name` (and its aliases) through `normalizeVendorKey`,
   * so it must spell the vendor the way the Waste Vendors tab does. The
   * dispatch dialog picks a vendor and an organization; the pair resolves to
   * a pipeline through `resolveWastePipelineForVendor`.
   */
  vendor: string;
  /** File upload slots — each slot becomes a separate upload in the UI */
  files: WastePipelineFileSlot[];
  /**
   * Set when the pipeline was built for one organization and carries that
   * organization's rules. It is the only pipeline that can run for that vendor
   * and tenant pair. Null marks the vendor's base pipeline, which any
   * organization may run for QC. Either way, ingestion waits for the data team
   * to approve the vendor and organization pair.
   */
  fixedTenant: FixedTenant | null;
  /**
   * Optional set of slot keys where the run must supply at least ONE of
   * them (independent of each slot's individual `required` flag). Used
   * when none of the slots are strictly required on their own but the
   * pipeline still needs at least one file. UI and resolver both enforce
   * this.
   */
  requireAtLeastOneOf?: readonly string[];
  /**
   * Tenant IDs that have pipeline-specific custom rules baked into the
   * transforms (e.g. an anomaly check that only fires for one tenant, or a
   * tenant-pending site that's rejected only when that tenant runs it).
   * The dispatch UI uses this to show a "Custom rules apply for this
   * tenant" indicator when the active org is in the list, so staff know to
   * expect divergent behavior vs the vendor-generic baseline.
   *
   * Kept intentionally minimal — just a flag. Detail on *which* rules apply
   * lives in the pipeline's spec/README, not here.
   */
  tenantsWithCustomRules?: readonly string[];
};

/** Biennial Data upload slots for `knauf-clean-harbors`, one per Knauf customer code. */
export const KNAUF_CLEAN_HARBORS_BIENNIAL_SLOT_KEYS = [
  "biennial1",
  "biennial2",
  "biennial3",
  "biennial4",
  "biennial5",
  "biennial6",
  "biennial7",
] as const;

export const WASTE_PIPELINE_REGISTRY = {
  "altana-univar": {
    label: "Altana Univar",
    vendor: "Univar",
    // Univar's SAP "lifecycle" export ships one workbook per generator rather
    // than one combined file, so the run takes several files of the same shape
    // and the loader concatenates them. The slots are interchangeable: scope
    // comes from each row's own `Generator EPA ID`, never from which slot the
    // file arrived in or what it was named (spec §1.1). Three slots covers the
    // generators in the feed today — a fourth site needs one more entry here
    // and nothing else, since the pipeline reads its slot keys off this list.
    //
    // Because the slots carry no site identity, the loader guards against the
    // same export being attached twice (hard failure) and against two workbooks
    // sharing a manifest line (per-row rejection) — either would otherwise
    // double a site's lines and quantities silently.
    //
    // None is required on its own, since a site that shipped nothing in a
    // quarter has no file, so `requireAtLeastOneOf` enforces that a run still
    // supplies at least one.
    //
    // Reportability: never stamped. `Reportable` and `Classification` are both
    // blank and the Compliance Engine derives them at manifest time.
    files: [
      {
        key: "file1",
        label: "Generator 1",
        accept: ".xlsx,.xls",
        required: false,
      },
      {
        key: "file2",
        label: "Generator 2",
        accept: ".xlsx,.xls",
        required: false,
      },
      {
        key: "file3",
        label: "Generator 3",
        accept: ".xlsx,.xls",
        required: false,
      },
    ],
    requireAtLeastOneOf: ["file1", "file2", "file3"],
    fixedTenant: { id: TENANT_IDS.ALTANA, name: "Altana" },
  },
  "amazon-clean-earth": {
    label: "Amazon Clean Earth",
    vendor: "Clean Earth",
    // Reportability: classification is never stamped and reportable is blank
    // (engine-derived) except the three determinations the engine can't make —
    // Universal Waste its resolver can't categorize, pharma profiles outside
    // MN, and the TX Class 1 tier (pending STEERS generator-type coverage).
    files: [
      {
        key: "source",
        label: "CE Monthly Reporting Package",
        accept: ".xlsx,.xls",
        required: true,
      },
    ],
    fixedTenant: { id: TENANT_IDS.AMAZON, name: "Amazon" },
  },
  "amazon-clean-harbors": {
    label: "Amazon Clean Harbors",
    vendor: "Clean Harbors",
    // Reportability: same posture as amazon-clean-earth — no classification
    // stamp; reportable blank except unresolvable Universal Waste, pharma
    // outside MN, and the TX Class 1 tier.
    //
    // Two mutually exclusive inputs: the biennial workbook (optionally enriched
    // by the monthly companion), or the weekly tracking file, which carries the
    // monthly enrichment columns inline and so needs no companion. Neither is
    // required on its own — `requireAtLeastOneOf` enforces that a run supplies
    // one of them, and the pipeline module rejects a run that mixes both.
    files: [
      { key: "biennial", label: "Biennial Data", accept: ".xlsx,.xls", required: false },
      { key: "monthly", label: "Monthly Data (Optional)", accept: ".xlsx,.xls", required: false },
      {
        key: "weeklyXlsx",
        label: "Weekly Tracking File (xlsx/xlsm)",
        accept: ".xlsx,.xlsm",
        required: false,
      },
    ],
    requireAtLeastOneOf: ["biennial", "weeklyXlsx"],
    fixedTenant: { id: TENANT_IDS.AMAZON, name: "Amazon" },
  },
  "clean-earth": {
    label: "Clean Earth",
    vendor: "Clean Earth",
    // Base for the Clean Earth Format A/B/C monthly workbook. Reportability and
    // Classification are never populated; the Compliance Engine derives both.
    // Runs with no tenant rules, so it adds no Waste Line Tags.
    // aspirus-clean-earth and phoenix-childrens-clean-earth are this pipeline
    // with a tenant's rules from `tenants/<tenant>/waste/clean-earth/`.
    files: [
      {
        key: "source",
        label: "Clean Earth Monthly Workbook",
        accept: ".xlsx,.xls",
        required: true,
      },
    ],
    fixedTenant: null,
  },
  "aspirus-clean-earth": {
    label: "Aspirus Clean Earth",
    vendor: "Clean Earth",
    // The clean-earth base with Aspirus's rules (name aliases, WI/MI tag
    // states). Reportability: PHRM rows take the value from Aspirus's
    // `phrmReportableByEpaId` rule; every other row is left blank for the
    // Compliance Engine.
    files: [
      {
        key: "source",
        label: "Clean Earth Monthly Workbook",
        accept: ".xlsx,.xls",
        required: true,
      },
    ],
    fixedTenant: { id: TENANT_IDS.ASPIRUS, name: "Aspirus" },
  },
  "phoenix-childrens-clean-earth": {
    label: "Phoenix Children's Clean Earth",
    vendor: "Clean Earth",
    // The clean-earth base with PCH's rules (AZ tag states, DOT Printed
    // Information from the source DOT ID). Reportability is left blank for
    // the Compliance Engine.
    files: [
      {
        key: "source",
        label: "Clean Earth Monthly Workbook",
        accept: ".xlsx,.xls",
        required: true,
      },
    ],
    fixedTenant: {
      id: TENANT_IDS.PHOENIX_CHILDREN_S_HOSPITAL,
      name: "Phoenix Children's Hospital",
    },
  },
  "aspirus-stericycle": {
    label: "Aspirus Stericycle",
    vendor: "Stericycle",
    // Reportability: WI rows keep their Infectious Waste "Reportable" stamp —
    // a tenant-feed assumption the Compliance Engine deliberately carries no
    // tag rule for. MI rows are blank; the engine derives them.
    files: [
      {
        key: "source",
        label: "Stericycle Monthly Workbook",
        accept: ".xlsx,.xls",
        required: true,
      },
    ],
    fixedTenant: { id: TENANT_IDS.ASPIRUS, name: "Aspirus" },
  },
  "phoenix-childrens-stericycle": {
    label: "Phoenix Children's Stericycle",
    vendor: "Stericycle",
    files: [
      {
        key: "source",
        label: "Stericycle Monthly Workbook",
        accept: ".xlsx,.xls",
        required: true,
      },
    ],
    fixedTenant: {
      id: TENANT_IDS.PHOENIX_CHILDREN_S_HOSPITAL,
      name: "Phoenix Children's Hospital",
    },
  },
  "safety-kleen": {
    label: "Safety-Kleen",
    vendor: "Safety-Kleen",
    files: [
      {
        key: "source",
        label: "SK Waste Tracking Summary (.xls)",
        accept: ".xls,.xlsx",
        required: true,
      },
    ],
    fixedTenant: null,
    // Reportability is left to the Compliance Engine for every tenant but
    // UPS, which is the only one whose Reportable column is exported.
    //
    // Tenants with tenant-specific Safety-Kleen rules:
    //   - UPS: TX Class 1 non-industrial anomaly (Section 3.5), the
    //     pending KYR000047704 Superfund-site rejection (Appendix A.1), and
    //     the exported reportability determination noted above.
    //   - AEG: per-state tagging rules (LIB in MI, Drained Used Oil
    //     Filters / Oily Wastes / Used Oil in FL); rules
    //     live under `apps/lambdas/data-pipeline/src/tenants/aeg/waste/safety-kleen/`.
    //   - Tesla: density from lbs/gal on every row, DOT hazard indicator
    //     mapped from the source, single-column vendor descriptions,
    //     Universal auto-tagging, and classification left to Encamp's
    //     post-ingestion ProcessManifest job.
    // The dispatch UI surfaces a "Custom rules apply" indicator when one
    // of these tenants is selected so reviewers expect divergent behavior.
    tenantsWithCustomRules: [TENANT_IDS.UPS, TENANT_IDS.ATLA_EQUIPMENT_GROUP, TENANT_IDS.TESLA],
  },
  "elk-environmental": {
    label: "Elk Environmental",
    vendor: "Elk Environmental",
    // Base pipeline for the customer-maintained Elk tracker. Classification
    // and reportability are left to the Compliance Engine.
    files: [
      {
        key: "source",
        label: "Elk Environmental Waste Tracker",
        accept: ".xlsx,.xls",
        required: true,
      },
    ],
    fixedTenant: null,
  },
  "howard-energy-elk-environmental": {
    label: "Howard Energy Elk Environmental",
    vendor: "Elk Environmental",
    files: [
      {
        key: "source",
        label: "HEP Waste Tracker",
        accept: ".xlsx,.xls",
        required: true,
      },
    ],
    fixedTenant: { id: TENANT_IDS.HOWARD_ENERGY_PARTNERS, name: "Howard Energy Partners" },
  },
  "knauf-waste-management": {
    label: "Knauf Waste Management",
    vendor: "Waste Management",
    // The WM workbook is the transactional source; the Profile List is the
    // authoritative Path-1 lookup (description/TSDF/method/tags) and MUST be
    // re-uploaded each run so it stays current (SME decision B1).
    files: [
      {
        key: "source",
        label: "WM Monthly Workbook",
        accept: ".xlsx,.xls",
        required: true,
      },
      {
        key: "profileList",
        label: "Knauf WM Profile List",
        accept: ".xlsx,.xls",
        required: true,
      },
    ],
    fixedTenant: { id: TENANT_IDS.KNAUF, name: "Knauf Insulation" },
  },
  "knauf-arcwood": {
    label: "Knauf Arcwood",
    vendor: "Arcwood",
    // Reportability: never stamped — the output column is blank by design and
    // the Compliance Engine derives it; the pipeline's qaReportable signal is
    // QA-workbook-only.
    //
    // The Arcwood portal's Shipping History download is an HTML table saved
    // with an .xls extension — upload the raw portal file as-is (Excel's
    // "file seems corrupt" warning on manual open is expected).
    files: [
      {
        key: "source",
        label: "Arcwood Shipping History (WS_SHIP_RPT.xls)",
        accept: ".xls,.html",
        required: true,
      },
    ],
    fixedTenant: { id: TENANT_IDS.KNAUF, name: "Knauf Insulation" },
  },
  "knauf-veolia": {
    label: "Knauf Veolia",
    vendor: "Veolia",
    // Reportability is left blank on every line; the Compliance Engine derives
    // it at manifest time. The pipeline's former code-based stamp was retired
    // once the engine's non-haz baseline made a blank mean "engine decides"
    // rather than collapsing to reportable.
    //
    // Veolia's portal exports Knauf's month as two separate reports (haz +
    // non-haz) with the same `WasteGenMgtFlat` schema; the loader concatenates
    // them before any dedup so a manifest whose lines split across the two
    // files is handled as one manifest. Neither slot is required on its own —
    // January 2026's non-haz export had zero data rows because all that
    // month's activity was hazardous — so `requireAtLeastOneOf` enforces that
    // a run still supplies at least one.
    files: [
      {
        key: "hazardous",
        label: "Haz Waste Management Report",
        accept: ".xlsx,.xls",
        required: false,
      },
      {
        key: "nonHazardous",
        label: "Non-Haz Waste Management Report",
        accept: ".xlsx,.xls",
        required: false,
      },
    ],
    requireAtLeastOneOf: ["hazardous", "nonHazardous"],
    fixedTenant: { id: TENANT_IDS.KNAUF, name: "Knauf Insulation" },
  },
  "knauf-clean-harbors": {
    label: "Knauf Clean Harbors",
    vendor: "Clean Harbors",
    // Reportability and classification are never stamped; the Compliance
    // Engine derives both.
    //
    // The portal exports one Biennial Data CSV per Knauf customer code, so the
    // run takes up to seven. The slots are interchangeable — the join keys on
    // manifest + line, not on which slot a file arrived in — and none is
    // required, since biennial data only covers RCRA-reportable streams and a
    // plant's file is often empty.
    files: [
      {
        key: "wasteTracking",
        label: "Waste Tracking Data CSV",
        accept: ".csv",
        required: true,
      },
      ...KNAUF_CLEAN_HARBORS_BIENNIAL_SLOT_KEYS.map((key, i) => ({
        key,
        label: `Biennial Data CSV ${i + 1}`,
        accept: ".csv",
        required: false,
      })),
    ],
    fixedTenant: { id: TENANT_IDS.KNAUF, name: "Knauf Insulation" },
  },
  "clean-harbors": {
    label: "Clean Harbors",
    vendor: "Clean Harbors",
    // Reportability: classification is never stamped and reportable is blank
    // (engine-derived) except Universal Waste the engine's resolver can't
    // categorize. No TX Class 1 stamp — that assumption is Amazon-specific;
    // the engine tiers Class 1 from the facility's STEERS generator type.
    // global-partners-clean-harbors reuses these transforms unchanged.
    files: [
      {
        key: "wasteTracking",
        label: "Waste Tracking Data CSV (monthly)",
        accept: ".csv",
        required: false,
      },
      { key: "biennial", label: "Biennial Data CSV (monthly)", accept: ".csv", required: false },
      {
        key: "weeklyXlsx",
        label: "Weekly Tracking File (xlsx/xlsm)",
        accept: ".xlsx,.xlsm",
        required: false,
      },
    ],
    fixedTenant: null,
  },
  "global-partners-clean-harbors": {
    label: "Global Partners Clean Harbors",
    vendor: "Clean Harbors",
    // Copy of the Clean Harbors monthly pipeline for Global Partners (see the
    // pipeline's PIPELINE_SPEC.md addendum). Monthly CSVs only — Global has no
    // weekly feed, so both slots are strictly required.
    // Reportability: inherits the base clean-harbors posture (blank except
    // unresolvable Universal Waste).
    files: [
      {
        key: "wasteTracking",
        label: "Waste Tracking Data CSV (monthly)",
        accept: ".csv",
        required: true,
      },
      { key: "biennial", label: "Biennial Data CSV (monthly)", accept: ".csv", required: true },
    ],
    fixedTenant: { id: TENANT_IDS.GLOBAL_PARTNERS, name: "Global Partners" },
  },
  "petro-star-clean-harbors": {
    label: "Petro Star Clean Harbors",
    vendor: "Clean Harbors",
    // Monthly CSVs only — Petro Star has no weekly feed, so both slots are
    // strictly required.
    files: [
      {
        key: "wasteTracking",
        label: "Waste Tracking Data CSV (monthly)",
        accept: ".csv",
        required: true,
      },
      { key: "biennial", label: "Biennial Data CSV (monthly)", accept: ".csv", required: true },
    ],
    fixedTenant: { id: TENANT_IDS.PETRO_STAR, name: "Petro Star" },
  },
  "ups-veolia": {
    label: "UPS Veolia",
    vendor: "Veolia",
    // Spec §1.1 lists two files per production run (one haz, one non-haz),
    // but both have the same `WasteGenMgtFlat` schema and downstream
    // transforms are identical. Neither slot is strictly required on its
    // own — `requireAtLeastOneOf` enforces that the run must still supply
    // at least one of them. UI and resolver both honor this rule.
    files: [
      {
        key: "hazardous",
        label: "Haz Waste Management Report",
        accept: ".xlsx,.xls",
        required: false,
      },
      {
        key: "nonHazardous",
        label: "Non-Haz Waste Management Report",
        accept: ".xlsx,.xls",
        required: false,
      },
    ],
    requireAtLeastOneOf: ["hazardous", "nonHazardous"],
    fixedTenant: { id: TENANT_IDS.UPS, name: "UPS" },
  },
  "ups-world-oil": {
    label: "UPS World Oil",
    vendor: "World Oil",
    files: [
      {
        key: "source",
        label: "World Oil Activity Report (.xlsx)",
        accept: ".xlsx,.xls",
        required: true,
      },
    ],
    fixedTenant: { id: TENANT_IDS.UPS, name: "UPS" },
  },
  tradebe: {
    label: "Tradebe",
    vendor: "Tradebe",
    // Base pipeline for Tradebe's manifest line export. Classification and
    // reportability are left to the Compliance Engine.
    files: [
      {
        key: "source",
        label: "Tradebe Manifest Export",
        accept: ".xlsx,.xls",
        required: true,
      },
    ],
    fixedTenant: null,
  },
  "dci-tradebe": {
    label: "DCI Tradebe",
    vendor: "Tradebe",
    // The Tradebe base pipeline with DCI's manifest-numbering policy and
    // Generator EPA ID correction.
    files: [
      {
        key: "source",
        label: "Tradebe Manifest Export",
        accept: ".xlsx,.xls",
        required: true,
      },
    ],
    fixedTenant: {
      id: TENANT_IDS.DEPOT_CONNECT_INTERNATIONAL,
      name: "Depot Connect International (DCI)",
    },
  },
} as const satisfies Record<string, WastePipelineConfig>;

export type WastePipelineName = keyof typeof WASTE_PIPELINE_REGISTRY;

export function isWastePipelineName(name: string): name is WastePipelineName {
  return name in WASTE_PIPELINE_REGISTRY;
}

export const WASTE_PIPELINE_NAMES =
  Object.keys(WASTE_PIPELINE_REGISTRY).filter(isWastePipelineName);

/**
 * Distinct vendors that have at least one pipeline, in registry order. This is
 * the dispatch dialog's Vendor dropdown.
 */
export const WASTE_PIPELINE_VENDORS: readonly string[] = [
  ...new Set(WASTE_PIPELINE_NAMES.map((name) => WASTE_PIPELINE_REGISTRY[name].vendor)),
];

function vendorMatches(config: WastePipelineConfig, vendor: string): boolean {
  return normalizeVendorKey(config.vendor) === normalizeVendorKey(vendor);
}

/** Every pipeline that parses `vendor`'s files, base and tenant-specific alike. */
export function getWastePipelinesForVendor(vendor: string): WastePipelineName[] {
  return WASTE_PIPELINE_NAMES.filter((name) =>
    vendorMatches(WASTE_PIPELINE_REGISTRY[name], vendor)
  );
}

/**
 * The vendor's base pipeline (`fixedTenant: null`), if it has one. Vendors
 * without a base pipeline can only run for the organizations their
 * tenant-specific pipelines were built for.
 */
export function getBaseWastePipelineForVendor(vendor: string): WastePipelineName | undefined {
  return getWastePipelinesForVendor(vendor).find(
    (name) => WASTE_PIPELINE_REGISTRY[name].fixedTenant === null
  );
}

/**
 * The one pipeline that runs for a vendor and organization pair: the
 * tenant-specific pipeline when the organization has one, otherwise the
 * vendor's base pipeline, otherwise nothing. A tenant-specific pipeline always
 * wins so its organization's rules cannot be skipped by running the base.
 */
export function resolveWastePipelineForVendor(
  vendor: string,
  tenantId: string
): WastePipelineName | undefined {
  const candidates = getWastePipelinesForVendor(vendor);
  return (
    candidates.find((name) => WASTE_PIPELINE_REGISTRY[name].fixedTenant?.id === tenantId) ??
    candidates.find((name) => WASTE_PIPELINE_REGISTRY[name].fixedTenant === null)
  );
}

/**
 * Widening accessor for the registry. `WASTE_PIPELINE_REGISTRY` is declared
 * `as const satisfies Record<...>`, which preserves literal types for fields
 * like `fixedTenant` but drops optional fields that aren't set on every
 * pipeline (e.g. `requireAtLeastOneOf`). Reading through this helper gives
 * the consumer the full `WastePipelineConfig` shape without an `as` cast.
 */
export function getWastePipelineConfig(name: WastePipelineName): WastePipelineConfig {
  return WASTE_PIPELINE_REGISTRY[name];
}
